/*
arc — the pure model behind angle-field's angle3d and arc3d. An angle is a
direction; an arc is a direction and a width. No DOM, no Babylon. The
reader-facing summary is angle-field's "The model" section.

Both wraps (wrapDegrees for deltas, normaliseDegrees for stored angles) are
owned by manipulator.ts and re-exported here.

An envelope EXPLAINS, it does not merely clamp: it is a first-class value the
widget can draw (the permitted sector and the blocked one), and clamping is what
happens after you can see the reason.
*/

import { normaliseDegrees, wrapDegrees } from './manipulator.js'

export { normaliseDegrees, wrapDegrees }

/**
 * A sector of a circle: which way it points, and how wide it is.
 *
 * `centre` is stored normalised into `[0, 360)`; `width` is `0..360`, where 360
 * is the whole circle and 0 is a bearing with no spread.
 */
export interface Arc {
  centre: number
  width: number
}

const clamp = (v: number, lo: number, hi: number): number =>
  v < lo ? lo : v > hi ? hi : v

/** A well-formed arc: centre normalised, width in `[0, 360]`. */
export function arcOf(centre: number, width: number): Arc {
  return {
    centre: normaliseDegrees(centre),
    width: clamp(Number.isFinite(width) ? width : 0, 0, 360),
  }
}

/** The whole circle — the identity envelope, and the default for "no limit". */
export const FULL_CIRCLE: Arc = { centre: 0, width: 360 }

/** Where the arc begins, going anticlockwise-to-clockwise through its centre. */
export const arcStart = (a: Arc): number =>
  normaliseDegrees(a.centre - a.width / 2)

/** Where the arc ends. */
export const arcEnd = (a: Arc): number =>
  normaliseDegrees(a.centre + a.width / 2)

/**
 * Is this bearing inside the arc?
 *
 * One line, and it wraps for free: the difference is taken signed into
 * `(-180, 180]`, so an arc straddling north is not a special case. As an
 * interval test this is four cases and a wrap, which is where a consumer's
 * hand-rolled version goes wrong.
 */
export function arcContains(a: Arc, angle: number): boolean {
  if (a.width >= 360) return true
  return Math.abs(wrapDegrees(angle - a.centre)) <= a.width / 2 + 1e-9
}

/**
 * Does `outer` wholly contain `inner`?
 *
 * The inner arc's centre must sit within the slack the widths leave — which is
 * both conditions at once, and the reason centre-and-width is the right shape.
 */
export function arcWithinArc(inner: Arc, outer: Arc): boolean {
  if (outer.width >= 360) return true
  if (inner.width > outer.width + 1e-9) return false
  const slack = (outer.width - inner.width) / 2
  return Math.abs(wrapDegrees(inner.centre - outer.centre)) <= slack + 1e-9
}

/**
 * The nearest permitted bearing, catching at a stop rather than jumping.
 *
 * Outside the arc, the answer is whichever END is nearer — never the far side.
 * Snapping by wrapped distance to the arc's CENTRE would teleport a handle
 * dragged a hair past one limit to the opposite stop, which reads as the
 * control fighting you.
 */
export function clampAngleToArc(angle: number, limits: Arc): number {
  if (arcContains(limits, angle)) return normaliseDegrees(angle)
  const start = arcStart(limits)
  const end = arcEnd(limits)
  const toStart = Math.abs(wrapDegrees(angle - start))
  const toEnd = Math.abs(wrapDegrees(angle - end))
  return toStart <= toEnd ? start : end
}

/** Width limits for an arc, and the sector it must lie inside. */
export interface ArcLimits {
  minWidth?: number
  maxWidth?: number
  /** The arc may only lie within this one. Defaults to the whole circle. */
  envelope?: Arc
}

/**
 * The nearest legal arc: width first, then position.
 *
 * In that order deliberately. Position a too-wide arc first and it can only be
 * wrong, because nothing about where it points can make it fit — narrowing is
 * the constraint that has to give, and doing it first leaves the centre free to
 * honour what the author was actually aiming at.
 */
export function clampArc(value: Arc, limits: ArcLimits = {}): Arc {
  const envelope = limits.envelope ?? FULL_CIRCLE
  const maxWidth = Math.min(limits.maxWidth ?? 360, envelope.width)
  const minWidth = Math.min(limits.minWidth ?? 0, maxWidth)
  const width = clamp(value.width, minWidth, maxWidth)
  if (envelope.width >= 360) return arcOf(value.centre, width)
  // The centre may move only within the slack the two widths leave.
  const slack = (envelope.width - width) / 2
  const off = wrapDegrees(value.centre - envelope.centre)
  return arcOf(envelope.centre + clamp(off, -slack, slack), width)
}

/** Which part of an arc a gesture has hold of. */
export type ArcGrip = 'start' | 'end' | 'centre'

/**
 * Which grip is nearest to a bearing, or null if none is within `reach`.
 *
 * The centre is tested LAST and loses ties. On a narrow arc all three grips are
 * within a few degrees of each other, and an edge you cannot grab is worse than
 * a rotation you have to reach for: rotating is also achievable by dragging an
 * edge and then the other, where a stuck width is simply stuck.
 */
export function nearestArcGrip(
  a: Arc,
  angle: number,
  reach = 12
): ArcGrip | null {
  const d = (target: number) => Math.abs(wrapDegrees(angle - target))
  const candidates: Array<[ArcGrip, number]> = [
    ['start', d(arcStart(a))],
    ['end', d(arcEnd(a))],
    ['centre', d(a.centre)],
  ]
  let best: ArcGrip | null = null
  let bestD = reach
  for (const [grip, dist] of candidates) {
    if (dist < bestD) {
      bestD = dist
      best = grip
    }
  }
  return best
}

/**
 * Apply a drag on `grip` to `value`, and return the new arc.
 *
 * - **an edge** changes the WIDTH, the other edge staying put
 * - **the centre** rotates the whole arc, the width staying put
 *
 * Dragging an edge past its opposite would invert the arc, which is never what
 * the gesture meant — the width stops at zero instead. Dragging one the whole
 * way round would wrap it to a hair's width; it saturates at the full circle
 * instead, for the same reason.
 */
export function dragArc(
  value: Arc,
  grip: ArcGrip,
  angle: number,
  limits: ArcLimits = {}
): Arc {
  if (grip === 'centre') {
    return clampArc({ centre: angle, width: value.width }, limits)
  }
  const fixed = grip === 'start' ? arcEnd(value) : arcStart(value)
  /*
  MEASURE THE NEW WIDTH FROM THE FIXED EDGE, IN THE ARC'S OWN DIRECTION.

  `wrapDegrees` would give the SHORT way round, which flips to the other side of
  the circle the moment the arc passes 180° wide — so a slow widen would snap to
  a narrow arc halfway through. `normaliseDegrees` of the directed difference
  keeps growth monotonic all the way to 360.
  */
  const width =
    grip === 'start'
      ? normaliseDegrees(fixed - angle)
      : normaliseDegrees(angle - fixed)
  const centre =
    grip === 'start'
      ? normaliseDegrees(fixed - width / 2)
      : normaliseDegrees(fixed + width / 2)
  return clampArc({ centre, width }, limits)
}

/**
 * The sector of `envelope` that is NOT permitted — what a widget draws as
 * blocked.
 *
 * Returns null when everything is allowed. The complement of an arc is another
 * arc: the same centre turned half a circle, with the leftover width. That it
 * is expressible at all is the payoff of centre-and-width, and it is what lets
 * the control show the ship rather than merely obey it.
 */
export function arcComplement(envelope: Arc): Arc | null {
  if (envelope.width >= 360) return null
  return arcOf(envelope.centre + 180, 360 - envelope.width)
}
